// Fuzzy room-name comparison, used for ANALYTICS ONLY. Room mapping is imperfect, so the same
// physical room reaches us under names that drift ("Deluxe" vs "Deluxe Queen") and lands in
// different mapping groups. This util answers "would a customer read these two names as the same
// room?" so we can measure how often a suggested alternative was beaten by a similar-looking
// cheaper deal elsewhere in the list.
//
// The booking-side check stays exact-match - see RecheckAlternative.compareRoom. Nothing here
// may influence which deal a user is sent to.

const DICE_MATCH_THRESHOLD = 0.6;

const WHITESPACE_REGEX = /\s+/g;
const NON_ALPHANUMERIC_REGEX = /[^a-z0-9]+/g;
const DIACRITICS_REGEX = /[\u0300-\u036f]/g;

// Words that carry no room identity - dropping them keeps "Double Room" and "Double" comparable.
const NOISE_WORDS = new Set(['room', 'rooms', 'with', 'and', 'the', 'a', 'an', 'in', 'of', 'for', 'plus']);

// Attributes that make two rooms genuinely different products rather than the same room named
// differently. A group only conflicts when BOTH names commit to it and neither commitment
// contains the other, so "Junior Suite" still matches "Suite" while "Twin" never matches "Double".
const CONFLICT_GROUPS = {
    bed: new Set(['single', 'twin', 'double', 'queen', 'king', 'triple', 'quad', 'quadruple', 'family']),
    class: new Set([
        'standard',
        'classic',
        'comfort',
        'superior',
        'deluxe',
        'executive',
        'premium',
        'luxury',
        'presidential',
        'junior',
        'suite',
        'studio',
        'apartment',
        'villa',
        'dorm',
    ]),
    view: new Set(['sea', 'ocean', 'city', 'garden', 'mountain', 'pool', 'courtyard', 'inland']),
};

const MATCHED_BY = {
    CONTAINS: 'contains',
    DICE: 'dice',
};

function tokenize(name) {
    if (typeof name !== 'string') {
        return new Set();
    }

    const normalized = name
        .normalize('NFD')
        .replace(DIACRITICS_REGEX, '')
        .toLowerCase()
        .replace(NON_ALPHANUMERIC_REGEX, ' ')
        .replace(WHITESPACE_REGEX, ' ')
        .trim();

    if (!normalized) {
        return new Set();
    }

    return new Set(normalized.split(' ').filter((token) => token && !NOISE_WORDS.has(token)));
}

function intersectionSize(a, b) {
    let size = 0;
    a.forEach((token) => {
        if (b.has(token)) {
            size += 1;
        }
    });
    return size;
}

function contains(outer, inner) {
    if (!inner.size) {
        return false;
    }
    return intersectionSize(inner, outer) === inner.size;
}

function eitherContains(a, b) {
    return contains(a, b) || contains(b, a);
}

function dice(a, b) {
    if (!a.size || !b.size) {
        return 0;
    }
    return (2 * intersectionSize(a, b)) / (a.size + b.size);
}

function restrictToGroup(tokens, group) {
    const restricted = new Set();
    tokens.forEach((token) => {
        if (group.has(token)) {
            restricted.add(token);
        }
    });
    return restricted;
}

function findConflictGroup(a_tokens, b_tokens) {
    for (const [group_name, group] of Object.entries(CONFLICT_GROUPS)) {
        const a_group = restrictToGroup(a_tokens, group);
        const b_group = restrictToGroup(b_tokens, group);

        if (!a_group.size || !b_group.size) {
            continue;
        }
        if (!eitherContains(a_group, b_group)) {
            return group_name;
        }
    }
    return null;
}

// { matched, score, matched_by, conflict_group } - `score` is always reported, even when a
// conflict blocked the match, so the threshold can be re-tuned in analysis without a deploy.
function compareRoomNames(a_name, b_name) {
    const a_tokens = tokenize(a_name);
    const b_tokens = tokenize(b_name);

    if (!a_tokens.size || !b_tokens.size) {
        return { matched: false, score: 0, matched_by: null, conflict_group: null };
    }

    const score = dice(a_tokens, b_tokens);
    const conflict_group = findConflictGroup(a_tokens, b_tokens);

    if (conflict_group) {
        return { matched: false, score, matched_by: null, conflict_group };
    }

    if (eitherContains(a_tokens, b_tokens)) {
        return { matched: true, score, matched_by: MATCHED_BY.CONTAINS, conflict_group: null };
    }

    if (score >= DICE_MATCH_THRESHOLD) {
        return { matched: true, score, matched_by: MATCHED_BY.DICE, conflict_group: null };
    }

    return { matched: false, score, matched_by: null, conflict_group: null };
}

module.exports = {
    DICE_MATCH_THRESHOLD,
    NOISE_WORDS,
    CONFLICT_GROUPS,
    MATCHED_BY,
    tokenize,
    compareRoomNames,
};
